import React from 'react';
import type { BarGraphSegmentConfig } from '../../../components/widgets/ServiceAreaBarGraphWidget.types';
import { COLOR_PALETTE } from '../../../components/widgets/shared/barGraphUtils';
import { TaskStage } from '../../../types/Task';
import type { ForemanAreaApprovalData } from '../../../types/ApprovalData';
import type { ForemanAreaNewWorkIdData } from '../hooks/useNewWorkIdCounts';
import type { TaskAssignmentForemanAreaRow } from '../hooks/useWorkIdTaskAssignmentSummary';

export const SUBMISSIONS_BAR_CONFIG: BarGraphSegmentConfig<ForemanAreaApprovalData> = {
  getServiceArea: (item) => item.foremanArea.serviceArea,
  getTotal: (item) => item.pendingApprovals + item.revisionsRequested,
  getForemanAreaId: (item) => item.foremanArea.id,
  getSubSegments: (item) => [
    { value: item.pendingApprovals },
    { value: item.revisionsRequested, darken: true },
  ],
  getSegmentTooltip: (item) => (
    <>
      <div className="tw-font-bold tw-mb-1">{item.foremanArea.name}</div>
      <div style={{ fontSize: '0.65rem' }}>
        Pending {item.pendingApprovals}, Revisions {item.revisionsRequested}
      </div>
    </>
  ),
  getGroupTooltip: (items, serviceAreaName) => (
    <>
      <div className="tw-font-bold tw-mb-1">{serviceAreaName}</div>
      {items.map((item) => (
        <div key={item.foremanArea.id} style={{ fontSize: '0.65rem' }}>
          {item.foremanArea.name}: Pending {item.pendingApprovals}, Revisions {item.revisionsRequested}
        </div>
      ))}
    </>
  ),
  emptyMessage: 'No pending approvals data available',
  getForemanAreaRedirect: (id) => `/submissions?status=pending&foremanArea=${id}`,
  getServiceAreaRedirect: (id) => `/submissions?status=pending&serviceAreaId=${id}`,
};

export const NEW_WORK_IDS_BAR_CONFIG: BarGraphSegmentConfig<ForemanAreaNewWorkIdData> = {
  getServiceArea: (item) => item.foremanArea.serviceArea,
  getTotal: (item) => item.newWorkIdCount + (item.approvedWorkIdCount ?? 0),
  getForemanAreaId: (item) => item.foremanArea.id,
  getSegmentTooltip: (item) => (
    <>
      <div className="tw-font-bold tw-mb-1">{item.foremanArea.name}</div>
      <div style={{ fontSize: '0.65rem' }}>
        New: {item.newWorkIdCount}, Approved: {item.approvedWorkIdCount ?? 0}
      </div>
    </>
  ),
  getGroupTooltip: (items, serviceAreaName) => (
    <>
      <div className="tw-font-bold tw-mb-1">{serviceAreaName}</div>
      {items.map((item) => (
        <div key={item.foremanArea.id} style={{ fontSize: '0.65rem' }}>
          {item.foremanArea.name}: New {item.newWorkIdCount}, Approved {item.approvedWorkIdCount ?? 0}
        </div>
      ))}
    </>
  ),
  emptyMessage: 'No new or approved work IDs',
  getForemanAreaRedirect: (id) => `/work-ids?status=New,Approved&foremanAreaId=${id}`,
  getServiceAreaRedirect: (id) => `/work-ids?status=New,Approved&serviceAreaId=${id}`,
};

/** Bar fill colors (match legend + home bar charts). Same reds as `COLOR_PALETTE`. Bottom → top: Planned, Active, AMAC, IR. */
export const TASK_ASSIGNMENT_COLOR_PLANNED = COLOR_PALETTE[3] ?? '#8B0000';
export const TASK_ASSIGNMENT_COLOR_ACTIVE = COLOR_PALETTE[0] ?? '#B85450';
export const TASK_ASSIGNMENT_COLOR_AMAC = COLOR_PALETTE[2] ?? '#E53935';
export const TASK_ASSIGNMENT_COLOR_INTERNAL_REVIEW = COLOR_PALETTE[1] ?? '#C62828';

const TASK_ASSIGNMENT_DRILL_STATUSES = [
  TaskStage.PLANNED,
  TaskStage.ACTIVE,
  TaskStage.ACTIVE_MARKED_AS_COMPLETE,
  TaskStage.INTERNAL_REVIEW,
].join(',');

/** Same order as `getSubSegments` on assignment stacked charts (slice index → status query). */
const TASK_ASSIGNMENT_SLICE_STAGES = [
  TaskStage.PLANNED,
  TaskStage.ACTIVE,
  TaskStage.ACTIVE_MARKED_AS_COMPLETE,
  TaskStage.INTERNAL_REVIEW,
] as const;

const TASK_ASSIGNMENT_SLICE_KEYS = [
  'planned',
  'active',
  'activeMarkedAsComplete',
  'internalReview',
] as const;

/** Legend chips for the Assigned Work ID stacked chart. */
export function TaskAssignmentStatusLegend(): React.ReactElement {
  const items = [
    { label: 'Planned', color: TASK_ASSIGNMENT_COLOR_PLANNED },
    { label: 'Active', color: TASK_ASSIGNMENT_COLOR_ACTIVE },
    { label: 'Active (marked complete)', color: TASK_ASSIGNMENT_COLOR_AMAC },
    { label: 'Internal review', color: TASK_ASSIGNMENT_COLOR_INTERNAL_REVIEW },
  ];
  return (
    <div
      className="tw-flex tw-flex-wrap tw-justify-center tw-items-center tw-gap-3 tw-px-2 tw-text-content-muted"
      style={{ fontSize: '0.75rem' }}
    >
      {items.map((it) => (
        <span key={it.label} className="tw-inline-flex tw-items-center tw-gap-1">
          <span
            aria-hidden
            style={{
              display: 'inline-block',
              width: 14,
              height: 14,
              borderRadius: 2,
              backgroundColor: it.color,
              flexShrink: 0,
              border: '1px solid rgba(0,0,0,0.08)',
            }}
          />
          {it.label}
        </span>
      ))}
      <span className="tw-text-content-muted tw-italic" style={{ fontSize: '0.7rem' }}>
        Stack bottom → top
      </span>
    </div>
  );
}

/** Quantified-assigned slices only → QP Tracking drill (and `taskId` when unambiguous). */
export const TASK_ASSIGNMENT_BAR_CONFIG_QUANTIFIED: BarGraphSegmentConfig<TaskAssignmentForemanAreaRow> = {
  getServiceArea: (item) => ({
    id: item.foremanArea.serviceArea.id,
    name: item.foremanArea.serviceArea.name,
    code: item.foremanArea.serviceArea.code ?? undefined,
  }),
  getTotal: (item) =>
    item.plannedCount + item.activeCount + item.activeMarkedCompleteCount + item.internalReviewCount,
  getForemanAreaId: (item) => item.foremanArea.id,
  getSubSegments: (item) => [
    { value: item.plannedCount, backgroundColor: TASK_ASSIGNMENT_COLOR_PLANNED },
    { value: item.activeCount, backgroundColor: TASK_ASSIGNMENT_COLOR_ACTIVE },
    { value: item.activeMarkedCompleteCount, backgroundColor: TASK_ASSIGNMENT_COLOR_AMAC },
    { value: item.internalReviewCount, backgroundColor: TASK_ASSIGNMENT_COLOR_INTERNAL_REVIEW },
  ],
  getSegmentTooltip: (item) => (
    <>
      <div className="tw-font-bold tw-mb-1">{item.foremanArea.name}</div>
      <div style={{ fontSize: '0.65rem' }}>
        Planned: {item.plannedCount}
        <br />
        Active: {item.activeCount}
        <br />
        Active (marked complete): {item.activeMarkedCompleteCount}
        <br />
        Internal review: {item.internalReviewCount}
      </div>
    </>
  ),
  getGroupTooltip: (items, serviceAreaName) => (
    <>
      <div className="tw-font-bold tw-mb-1">{serviceAreaName}</div>
      {items.map((row) => (
        <div key={row.foremanArea.id} style={{ fontSize: '0.65rem' }}>
          {row.foremanArea.name}: Planned {row.plannedCount}, active {row.activeCount}, marked complete{' '}
          {row.activeMarkedCompleteCount}, internal review {row.internalReviewCount}
        </div>
      ))}
    </>
  ),
  emptyMessage: 'No Quantified-assigned tasks in planned / active / review for this filter',
  /** Fallback when charts do not use per-slice drill (assignment chart uses `getForemanAreaSubSegmentRedirect`). */
  getForemanAreaRedirect: (_id, row?: TaskAssignmentForemanAreaRow) => {
    const qs = new URLSearchParams();
    qs.set('status', TASK_ASSIGNMENT_DRILL_STATUSES);
    if (row) qs.set('foremanArea', row.foremanArea.name);
    return `/tasks/quantified?${qs.toString()}`;
  },
  getForemanAreaSubSegmentRedirect: (_id, row, subSegmentIndex) => {
    const stage = TASK_ASSIGNMENT_SLICE_STAGES[subSegmentIndex];
    const key = TASK_ASSIGNMENT_SLICE_KEYS[subSegmentIndex];
    const drill = key ? row.drillTargets[key] : undefined;

    if (drill?.singleTaskId && drill.singleTaskType === 'Quantified') {
      const qs = new URLSearchParams();
      qs.set('taskId', String(drill.singleTaskId));
      return `/tasks/quantified?${qs.toString()}`;
    }

    const qs = new URLSearchParams();
    qs.set('status', stage ?? TASK_ASSIGNMENT_DRILL_STATUSES);
    qs.set('foremanArea', row.foremanArea.name);
    return `/tasks/quantified?${qs.toString()}`;
  },
  getServiceAreaRedirect: (_id, area?: { id: number; name: string }) => {
    const qs = new URLSearchParams();
    qs.set('status', TASK_ASSIGNMENT_DRILL_STATUSES);
    if (area?.name) qs.set('serviceArea', area.name);
    return `/tasks/quantified?${qs.toString()}`;
  },
};

/** Routine `Task.type` only → Routine Work drill. */
export const TASK_ASSIGNMENT_BAR_CONFIG_ROUTINE: BarGraphSegmentConfig<TaskAssignmentForemanAreaRow> = {
  ...TASK_ASSIGNMENT_BAR_CONFIG_QUANTIFIED,
  emptyMessage: 'No Routine-assigned tasks in planned / active / review for this filter',
  getForemanAreaRedirect: (_id, row?: TaskAssignmentForemanAreaRow) => {
    const qs = new URLSearchParams();
    qs.set('status', TASK_ASSIGNMENT_DRILL_STATUSES);
    if (row) qs.set('foremanArea', row.foremanArea.name);
    return `/tasks/routine?${qs.toString()}`;
  },
  getForemanAreaSubSegmentRedirect: (_id, row, subSegmentIndex) => {
    const stage = TASK_ASSIGNMENT_SLICE_STAGES[subSegmentIndex];
    const key = TASK_ASSIGNMENT_SLICE_KEYS[subSegmentIndex];
    const drill = key ? row.drillTargets[key] : undefined;

    if (drill?.singleTaskId && drill.singleTaskType === 'Routine') {
      const qs = new URLSearchParams();
      qs.set('taskId', String(drill.singleTaskId));
      return `/tasks/routine?${qs.toString()}`;
    }

    const qs = new URLSearchParams();
    qs.set('status', stage ?? TASK_ASSIGNMENT_DRILL_STATUSES);
    qs.set('foremanArea', row.foremanArea.name);
    return `/tasks/routine?${qs.toString()}`;
  },
  getServiceAreaRedirect: (_id, area?: { id: number; name: string }) => {
    const qs = new URLSearchParams();
    qs.set('status', TASK_ASSIGNMENT_DRILL_STATUSES);
    if (area?.name) qs.set('serviceArea', area.name);
    return `/tasks/routine?${qs.toString()}`;
  },
};
